/** Step-up ("sudo mode") for sensitive self-service actions
 *  (tenancy/stepup.py): changing the password and managing MFA factors
 *  require a RECENT factor proof once the user holds any factor.
 *
 *  Usage: wrap the page in <StepUpProvider>, then run sensitive calls
 *  through the hook:
 *
 *      const withStepUp = useStepUp()
 *      await withStepUp((token) => api.deletePasskey(uid, token ?? undefined))
 *
 *  The call runs bare first; a 403 step_up_required opens the modal
 *  (TOTP, passkey ceremony, or recovery code — whatever the user holds),
 *  and the call is retried with the minted token in the X-Step-Up header.
 *  Cancelling the modal rejects with StepUpCancelled — callers should
 *  treat it as a no-op, not an error to display.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError } from '../../api/client'
import type { MfaMethod, StepUpChallengeResult } from '../../api/types'
import { passkeyErrorMessage, runPasskeyAuthentication } from '../../lib/webauthn'
import { Button, Input, Modal } from '../ui'

export class StepUpCancelled extends Error {
  constructor() {
    super('Step-up verification cancelled')
    this.name = 'StepUpCancelled'
  }
}

/** Runs `fn` with an optional X-Step-Up token, opening the modal on a
 *  403 step_up_required and retrying once verification completes. */
export type WithStepUp = <T>(fn: (token: string | null) => Promise<T>) => Promise<T>

/** Provider-less fallback: run bare (no modal possible). */
const StepUpContext = createContext<WithStepUp>((fn) => fn(null))

export function useStepUp(): WithStepUp {
  return useContext(StepUpContext)
}

type PendingRequest = {
  methods: MfaMethod[]
  resolve: (token: string | null) => void
}

/** Small lock glyph marking step-up-protected actions. */
export function StepUpLockHint() {
  return (
    <span className="inline-flex items-center gap-1 text-ostrel-ink-muted" title="Protected by step-up verification">
      <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1a3 3 0 0 0-3 3v2H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3Zm-1.5 5V4a1.5 1.5 0 0 1 3 0v2h-3Z" />
      </svg>
      <span className="text-[9px] font-semibold uppercase tracking-widest">step-up</span>
    </span>
  )
}

function StepUpModal({
  methods,
  onDone,
  onCancel,
}: {
  methods: MfaMethod[]
  onDone: (token: string) => void
  onCancel: () => void
}) {
  const [challenge, setChallenge] = useState<StepUpChallengeResult | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [mode, setMode] = useState<'totp' | 'recovery'>(methods.includes('totp') ? 'totp' : 'recovery')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The challenge is single-use and consumed by EVERY verify attempt — a
  // wrong code means starting a fresh one before the user can retry.
  const refreshChallenge = useCallback(() => {
    setBusy(true)
    setError(null)
    api
      .stepUpChallenge()
      .then(setChallenge)
      .catch((e) => setLoadError(e instanceof ApiError ? e.detail : 'Could not start verification'))
      .finally(() => setBusy(false))
  }, [])

  useEffect(refreshChallenge, [refreshChallenge])

  async function submitCode(e: React.FormEvent) {
    e.preventDefault()
    if (!challenge || busy || !code.trim()) return
    setBusy(true)
    setError(null)
    try {
      const body =
        mode === 'totp' ? { challenge_id: challenge.challenge_id, totp_code: code.trim() } : { challenge_id: challenge.challenge_id, recovery_code: code.trim() }
      const { step_up_token } = await api.stepUpVerify(body)
      onDone(step_up_token)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? 'Invalid code — try again.' : err instanceof ApiError ? err.detail : 'Verification failed')
      setCode('')
      refreshChallenge()
    } finally {
      setBusy(false)
    }
  }

  async function usePasskey() {
    if (!challenge || busy) return
    setBusy(true)
    setError(null)
    try {
      const { challenge_id, options } = await api.passkeyLoginOptions({ step_up_challenge: challenge.challenge_id })
      const credential = await runPasskeyAuthentication(options)
      const { step_up_token } = await api.passkeyLoginVerify({ challenge_id, credential })
      if (!step_up_token) throw new Error('Passkey verification failed.')
      onDone(step_up_token)
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : passkeyErrorMessage(err))
      refreshChallenge()
    } finally {
      setBusy(false)
    }
  }

  const canTotp = methods.includes('totp')
  const canPasskey = methods.includes('passkey')
  const canRecovery = methods.includes('recovery')

  return (
    <Modal open title="Confirm it's you" onClose={onCancel}>
      <div className="flex flex-col gap-4">
        <p className="text-xs leading-relaxed text-ostrel-ink-muted">
          This action is protected by step-up verification — prove a second factor to continue.
        </p>

        {loadError ? (
          <p className="text-xs text-ostrel-danger">{loadError}</p>
        ) : !challenge ? (
          <p className="text-xs text-ostrel-ink-muted">Starting verification…</p>
        ) : (
          <>
            {(canTotp || canRecovery) && (
              <div className="flex gap-2">
                {canTotp && (
                  <Button variant={mode === 'totp' ? 'navy' : 'ghost'} size="sm" onClick={() => { setMode('totp'); setCode(''); setError(null) }}>
                    Authenticator code
                  </Button>
                )}
                {canRecovery && (
                  <Button
                    variant={mode === 'recovery' ? 'navy' : 'ghost'}
                    size="sm"
                    onClick={() => { setMode('recovery'); setCode(''); setError(null) }}
                  >
                    Recovery code
                  </Button>
                )}
              </div>
            )}

            {(canTotp || canRecovery) && (
              <form className="flex flex-col gap-3" onSubmit={(e) => void submitCode(e)}>
                <Input
                  label={mode === 'totp' ? 'Code from your authenticator app' : 'One of your recovery codes'}
                  inputMode={mode === 'totp' ? 'numeric' : 'text'}
                  autoComplete={mode === 'totp' ? 'one-time-code' : 'off'}
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <Button type="submit" variant="navy" size="sm" disabled={!code.trim() || busy}>
                  {busy ? 'Verifying…' : 'Verify'}
                </Button>
              </form>
            )}

            {canPasskey && (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => void usePasskey()}>
                Use a passkey instead
              </Button>
            )}
          </>
        )}

        {error && <p className="text-xs text-ostrel-danger">{error}</p>}
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingRequest | null>(null)

  const withStepUp = useCallback<WithStepUp>(async (fn) => {
    try {
      return await fn(null)
    } catch (err) {
      if (!(err instanceof ApiError) || !err.isStepUpRequired) throw err
      const token = await new Promise<string | null>((resolve) => {
        setPending({ methods: (err.methods ?? []) as MfaMethod[], resolve })
      })
      if (token === null) throw new StepUpCancelled()
      return await fn(token)
    }
  }, [])

  return (
    <StepUpContext.Provider value={withStepUp}>
      {children}
      {pending && (
        <StepUpModal
          methods={pending.methods}
          onDone={(token) => {
            setPending(null)
            pending.resolve(token)
          }}
          onCancel={() => {
            setPending(null)
            pending.resolve(null)
          }}
        />
      )}
    </StepUpContext.Provider>
  )
}
